import { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import WorkerPremiumCard, { WorkerPremiumCardHeader } from './WorkerPremiumCard';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { CheckCircle, Eye, FileText, Loader2, Shield } from 'lucide-react';
import API_BASE from '../../utils/apiBase';
import { formatBackendDate } from '../../lib/dateUtils';

const API = API_BASE;

export default function WorkerAssignedPoliciesCard({
  policies = [],
  token,
  onRefresh,
}) {
  const [loading, setLoading] = useState({});

  const pending = policies.filter((row) => row.needs_action);

  const openPolicyFile = async (policy, suffix = '') => {
    const key = `view-${policy.assignment_id}${suffix}`;
    setLoading((prev) => ({ ...prev, [key]: true }));
    try {
      if (policy.status === 'assigned') {
        await axios.put(`${API}/policy-assignments/${policy.assignment_id}/view`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      const endpoint = suffix === '-flow'
        ? `${API}/policies/${policy.policy_id}/flow-chart/file`
        : `${API}/policies/${policy.policy_id}/file`;
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      window.open(url, '_blank');
      onRefresh?.();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(suffix ? 'Flow chart not available yet' : 'Policy document not found');
      } else {
        toast.error('Failed to open document');
      }
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleAcknowledge = async (policy) => {
    const signerName = window.prompt('Type your full name to acknowledge this policy:');
    if (signerName === null) return;
    if (!signerName.trim()) {
      toast.error('Full name is required');
      return;
    }

    const key = `ack-${policy.assignment_id}`;
    setLoading((prev) => ({ ...prev, [key]: true }));
    try {
      await axios.put(
        `${API}/policy-assignments/${policy.assignment_id}/acknowledge`,
        { signer_name: signerName.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Policy acknowledged');
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge policy');
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (!policies.length) {
    return null;
  }

  return (
    <WorkerPremiumCard data-testid="assigned-policies-section">
      <WorkerPremiumCardHeader
        icon={Shield}
        title="Organisation Policies"
        description="Read each assigned policy, then acknowledge that you understand it."
        action={pending.length > 0 ? (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
            {pending.length} action{pending.length === 1 ? '' : 's'} needed
          </Badge>
        ) : null}
      />
      <div className="space-y-3">
        {policies.map((policy) => (
          <div
            key={policy.assignment_id}
            className={`rounded-xl border p-4 ${
              policy.needs_action ? 'border-amber-200 bg-amber-50/60' : 'border-[#E4E8EB] bg-[#F8FAFA]'
            }`}
            data-testid={`worker-policy-${policy.assignment_id}`}
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-text-primary">{policy.policy_title}</p>
                  <span className="text-xs px-2 py-0.5 rounded bg-white border text-text-muted">
                    v{policy.policy_version || '1.0'}
                  </span>
                  {policy.needs_action ? (
                    <Badge variant="outline" className="border-amber-300 text-amber-700">Acknowledgement required</Badge>
                  ) : (
                    <Badge variant="outline" className="border-green-300 text-green-700">Acknowledged</Badge>
                  )}
                </div>
                <p className="text-xs text-text-muted mt-1">
                  Assigned {formatBackendDate(policy.assigned_at)}
                  {policy.acknowledged_at ? ` · Acknowledged ${formatBackendDate(policy.acknowledged_at)}` : ''}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-lg"
                  onClick={() => openPolicyFile(policy)}
                  disabled={loading[`view-${policy.assignment_id}`]}
                >
                  {loading[`view-${policy.assignment_id}`] ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Eye className="h-4 w-4 mr-1" />
                  )}
                  View policy
                </Button>
                {policy.has_flow_chart && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-lg"
                    onClick={() => openPolicyFile(policy, '-flow')}
                    disabled={loading[`view-${policy.assignment_id}-flow`]}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Flow chart
                  </Button>
                )}
                {policy.needs_action && (
                  <Button
                    size="sm"
                    className="rounded-lg bg-primary hover:bg-primary-hover text-white"
                    onClick={() => handleAcknowledge(policy)}
                    disabled={loading[`ack-${policy.assignment_id}`]}
                  >
                    {loading[`ack-${policy.assignment_id}`] ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-1" />
                    )}
                    I have read and understood
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </WorkerPremiumCard>
  );
}